/**
 * 与 ruoyi-vue-plus 后端约定的统一响应/分页/登录 DTO
 * 路径 @/types/api/index 同时也是 OpenAPI 生成类型 (./schema.d.ts) 的承载目录
 */

/** 通用响应包装 */
export interface R<T> {
  code: number
  msg: string
  data: T | null
}

/** 列表分页响应 (后端 TableDataInfo<T>) */
export interface TableDataInfo<T> {
  code: number
  msg: string
  total: number
  rows: T[]
}

/** 列表分页请求 (后端 PageQuery) */
export interface PageQuery {
  pageNum?: number
  pageSize?: number
  orderByColumn?: string
  isAsc?: 'asc' | 'desc'
}

/** C 端统一登录方式编码 */
export type ClientLoginMethodCode = 'password' | 'sms' | 'wechat'

/** POST /app/auth/login 通用请求体 */
export interface ClientLoginBody<TPayload extends object> {
  clientId: string
  tenantId?: string
  method: ClientLoginMethodCode
  /** 仅短信和微信首次注册时使用的推广邀请码 */
  inviteCode?: string
  payload: TPayload
}

/** 短信登录 payload */
export interface ClientSmsLoginPayload {
  phonenumber: string
  smsCode: string
}

/** 账号密码登录 payload */
export interface ClientPasswordLoginPayload {
  username: string
  password: string
  code?: string
  uuid?: string
}

/** 微信登录 payload */
export interface ClientWechatLoginPayload {
  ticket: string
}

export type SmsLoginBody = ClientLoginBody<ClientSmsLoginPayload> & { method: 'sms' }
export type PasswordLoginBody = ClientLoginBody<ClientPasswordLoginPayload> & {
  method: 'password'
}
export type WechatLoginBody = ClientLoginBody<ClientWechatLoginPayload> & { method: 'wechat' }

/** /auth/code 图形验证码响应体 */
export interface CaptchaVo {
  captchaEnabled?: boolean
  uuid?: string
  img?: string
}

export type WechatQrSceneStatus = 'pending' | 'confirmed' | 'expired' | 'consumed'

/** 公众号通知渠道展示资料 */
export interface NotifyWechatMpProfileVo {
  channelConfigId?: string | null
  appid?: string | null
  displayName?: string | null
  displayDescription?: string | null
  followGuideText?: string | null
  qrCodeUrl?: string | null
}

/** /app/notify/wechat-mp/status 响应体 */
export interface NotifyWechatMpStatusVo {
  followStatus: 'unknown' | 'not_bound' | 'following' | 'not_following' | 'check_failed'
  action: 'none' | 'bind' | 'follow' | 'retry' | 'select_account'
  notificationEnabled: boolean
  canReceiveNotification: boolean
  reason?: string | null
  boundAt?: string | null
  profile?: NotifyWechatMpProfileVo | null
  accountOptions: NotifyWechatMpProfileVo[]
}

/** /app/notify/wechat-mp/bind-session 响应体 */
export interface NotifyWechatMpBindSessionVo {
  bindingTicket?: string | null
  expireAt?: string | null
  webViewUrl?: string | null
  profile?: NotifyWechatMpProfileVo | null
}

/** /system/user/getInfo 响应体 (节选, 完整版后续由 OpenAPI 生成) */
export interface UserInfoVo {
  user: {
    userId: string
    username: string
    nickName: string
    avatar?: string
    phonenumber?: string
    email?: string
  }
  roles: string[]
  permissions: string[]
}

/** 业务异常 (与 R.code !== 200 / HTTP 4xx 5xx 对应) */
export class BizError extends Error {
  code: number
  constructor(code: number, msg: string) {
    super(msg)
    this.code = code
    this.name = 'BizError'
  }
}

// 后端 swagger 派生类型 (openapi-typescript 生成) 的前端友好层
export * from './schemas'
